import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Crown, Edit2, Check, X, Loader2, Trophy, Heart, ArrowUpRight, Copy, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Link } from 'react-router-dom';

/**
 * BRAVX Points dashboard for the logged-in user.
 * - Shows current points + lifetime + rank
 * - Username editor with live availability check
 * - Recent referral activity (both as referrer and as buyer)
 * - Shareable @ + copy link to invite friends
 */
export default function MyBravxSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [checkState, setCheckState] = useState('idle'); // idle | searching | available | taken | invalid
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const res = await api.get('/bravx/me');
      setData(res.data);
      setDraft(res.data?.username || '');
    } catch (e) {
      toast.error('No pudimos cargar tus BRAVX points');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Live username availability check while editing
  useEffect(() => {
    if (!editing) return;
    const v = (draft || '').trim().toLowerCase();
    if (!v || v === data?.username) { setCheckState('idle'); return; }
    if (v.length < 3 || !/^[a-z0-9._]+$/.test(v)) {
      setCheckState('invalid'); return;
    }
    setCheckState('searching');
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/bravx/check-username?username=${encodeURIComponent(v)}`);
        setCheckState(r.data?.available ? 'available' : 'taken');
      } catch {
        setCheckState('invalid');
      }
    }, 350);
    return () => clearTimeout(t);
  }, [draft, editing, data?.username]);

  const saveUsername = async () => {
    if (checkState !== 'available' && draft.trim() !== data?.username) return;
    setSaving(true);
    try {
      const r = await api.put('/bravx/me/username', { username: draft.trim() });
      toast.success('Username actualizado');
      setData((prev) => ({ ...prev, username: r.data.username }));
      setEditing(false);
    } catch (e) {
      const msg = e.response?.data?.detail || 'No se pudo actualizar';
      toast.error(typeof msg === 'string' ? msg : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const copyUsername = () => {
    if (!data?.username) return;
    navigator.clipboard.writeText('@' + data.username);
    toast.success('@username copiado');
  };

  const shareLink = () => {
    if (!data?.username) return;
    const url = `${window.location.origin}/u/${data.username}`;
    if (navigator.share) {
      navigator.share({ title: 'Mi perfil Bravx', text: `Soy @${data.username} en Bravo. Apoyame en tu próxima compra y sumamos puntos los dos.`, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-white/40" data-testid="bravx-section-loading">
        <Loader2 className="mx-auto animate-spin mb-2" size={20} />
        Cargando…
      </div>
    );
  }

  const s = data?.settings || {};
  const formula = `${s.points_per_threshold || 1} pto cada $${s.amount_threshold || 100}`;

  return (
    <div className="space-y-6" data-testid="bravx-section">
      {/* Hero stats */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.10] via-transparent to-transparent p-6"
      >
        <div
          className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-30"
          style={{ background: '#D4AF37' }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2 flex items-center gap-1.5"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              <Sparkles size={12} /> Tus BRAVX Points
            </p>
            <p
              className="text-6xl sm:text-7xl font-black text-white tracking-tighter leading-none"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
              data-testid="bravx-points-display"
            >
              {(data?.points || 0).toLocaleString('es-AR')}
            </p>
            <p className="text-xs text-white/40 mt-2">
              Histórico: {(data?.points_lifetime || 0).toLocaleString('es-AR')} pts · {data?.referrals_count || 0} canjes · Ranking #{data?.rank || '—'}
            </p>
          </div>
          <Link
            to="/bravx/top"
            className="self-start sm:self-end inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-bold uppercase tracking-wider text-white"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="bravx-view-ranking"
          >
            <Trophy size={12} /> Ver ranking
          </Link>
        </div>
      </motion.div>

      {/* Username editor */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-white flex items-center gap-2">
            <Crown size={14} className="text-[#D4AF37]" /> Tu @usuario
          </h4>
          {!editing && (
            <button
              type="button"
              onClick={() => { setDraft(data?.username || ''); setEditing(true); }}
              className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1"
              data-testid="bravx-edit-username"
            >
              <Edit2 size={11} /> Editar
            </button>
          )}
        </div>

        {!editing ? (
          <div className="flex flex-wrap items-center gap-3">
            <p
              className="text-2xl font-black text-white"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
              data-testid="bravx-username-display"
            >
              @{data?.username || '—'}
            </p>
            <button onClick={copyUsername} className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white" data-testid="bravx-copy-username">
              <Copy size={12} /> Copiar
            </button>
            <button onClick={shareLink} className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white" data-testid="bravx-share-link">
              <Share2 size={12} /> Compartir link
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">@</span>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/^@/, '').replace(/[^a-z0-9._]/gi, '').toLowerCase().slice(0, 24))}
                placeholder="tu.username"
                className="pl-7 bg-black/40 border-white/10"
                data-testid="bravx-username-input"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className={`text-[11px] ${
                checkState === 'available' ? 'text-emerald-400' :
                checkState === 'taken' ? 'text-red-400' :
                checkState === 'invalid' ? 'text-amber-400' :
                'text-white/40'
              }`}>
                {checkState === 'available' && '✓ Disponible'}
                {checkState === 'taken' && '✗ Ya está en uso'}
                {checkState === 'invalid' && 'Letras, números, puntos o _ · 3–24 caracteres'}
                {checkState === 'searching' && 'Buscando…'}
                {checkState === 'idle' && 'Tu @ va a aparecer cuando otros compren tickets y te apoyen.'}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setEditing(false); setDraft(data?.username || ''); }}
                  className="h-8 text-xs"
                >
                  <X size={12} className="mr-1" /> Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={saveUsername}
                  disabled={saving || (checkState !== 'available' && draft.trim() !== data?.username)}
                  className="h-8 text-xs bg-[#D4AF37] text-black hover:bg-[#F5D25A]"
                  data-testid="bravx-save-username"
                >
                  {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />}
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        )}

        <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
          Formula actual: <span className="text-[#D4AF37]">{formula}</span>. Ganás puntos cuando alguien usa tu @ al comprar.
        </p>
      </div>

      {/* Activity */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Heart size={14} className="text-[#D4AF37]" /> Actividad reciente
        </h4>
        {(!data?.activity || data.activity.length === 0) ? (
          <div className="text-center py-10 text-white/40 text-xs">
            Todavía no hay canjes. Compartí tu @ con un amigo!
          </div>
        ) : (
          <ul className="space-y-2" data-testid="bravx-activity-list">
            {data.activity.slice(0, 20).map((a) => {
              const isSelfPurchase = a.type === 'self_purchase' || !a.referrer_username;
              const isReferrer = !isSelfPurchase && a.referrer_username === data.username;
              const pts = isReferrer ? a.points_awarded_to_referrer : a.points_awarded_to_buyer;
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-black/30 border border-white/5"
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: isReferrer ? '#D4AF3725' : isSelfPurchase ? '#3b82f625' : '#22c55e25',
                      color: isReferrer ? '#D4AF37' : isSelfPurchase ? '#60a5fa' : '#22c55e',
                    }}
                  >
                    {isReferrer ? <Sparkles size={14} /> : isSelfPurchase ? <ArrowUpRight size={14} /> : <Heart size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">
                      {isReferrer
                        ? <>Apoyaron tu @ — <span className="text-white/60">{a.buyer_name || a.buyer_email}</span></>
                        : isSelfPurchase
                          ? <>Compra realizada</>
                          : <>Usaste @{a.referrer_username}</>}
                    </p>
                    <p className="text-[11px] text-white/40 truncate">
                      {a.event_title || '—'} · ${Math.round(a.amount).toLocaleString('es-AR')}
                    </p>
                  </div>
                  <span
                    className="text-sm font-black text-[#D4AF37]"
                    style={{ fontFamily: "'Unbounded', sans-serif" }}
                  >
                    +{pts}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* What can I do with points */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h4 className="text-sm font-bold text-white mb-3">¿Para qué sirven los puntos?</h4>
        <ul className="space-y-2 text-xs text-white/60">
          <li className="flex items-start gap-2"><Trophy size={12} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /> Top Bravx del mes en el ranking público.</li>
          <li className="flex items-start gap-2"><ArrowUpRight size={12} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /> Drops, sorteos y descuentos exclusivos (próximamente).</li>
          <li className="flex items-start gap-2"><Crown size={12} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /> Entradas gratis y beneficios en FULL BRAVX.</li>
        </ul>
      </div>
    </div>
  );
}
